import { z } from 'zod';
import { PointSchema } from './types';
import { HpTierSchema, ActiveConditionSchema } from './ai-types';
import { VisionConfigSchema, LightPresetSchema, DEFAULT_VISION } from './vision-types';
/**
 * Token Types
 *
 * Visual representation of combatants on the map.
 * Tokens connect to Combatant data but have their own visual properties.
 */
// =============================================================================
// TOKEN VISUALS
// =============================================================================
/**
 * Token shape for rendering
 */
export const TokenShapeSchema = z.enum([
    'circle', // Default for most tokens
    'square', // Constructs, objects
    'hex', // Special creatures
    'diamond', // Bosses
]);
/**
 * Token size (in grid squares)
 */
export const TokenSizeSchema = z.enum([
    'tiny', // 0.5 squares (pixies, sprites)
    'small', // 0.75 squares (goblins, halflings)
    'medium', // 1 square (humans, orcs)
    'large', // 2x2 squares (ogres, horses)
    'huge', // 3x3 squares (giants, dragons)
    'gargantuan', // 4x4+ squares (ancient dragons, tarrasque)
]);
/**
 * Size to grid squares mapping
 */
export const TOKEN_SIZE_SQUARES = {
    tiny: 0.5,
    small: 0.75,
    medium: 1,
    large: 2,
    huge: 3,
    gargantuan: 4,
};
/**
 * Token border style for status indication
 */
export const TokenBorderSchema = z.enum([
    'none',
    'selected', // Currently selected
    'targeted', // Targeted by action
    'turn', // Current turn in initiative
    'concentrating', // Maintaining concentration
    'dying', // Death saves
]);
// =============================================================================
// TOKEN DATA
// =============================================================================
/**
 * Token visual configuration
 */
export const TokenVisualsSchema = z.object({
    shape: TokenShapeSchema.default('circle'),
    size: TokenSizeSchema.default('medium'),
    color: z.string().default('#d4af37'), // Gold default
    borderColor: z.string().optional(),
    imageUrl: z.string().url().optional(), // Token art
    label: z.string().optional(), // Short label (initials, number)
    showHpBar: z.boolean().default(true),
    showConditionIcons: z.boolean().default(true),
});
/**
 * Token type — normal tokens vs special variants (e.g., Echo Knight echo)
 */
export const TokenTypeSchema = z.enum(['normal', 'echo']);
/**
 * Token - visual representation on the map
 */
export const TokenSchema = z.object({
    id: z.string(),
    // Link to game data
    combatantId: z.string().optional(), // Links to Combatant if in combat
    name: z.string(),
    tokenType: TokenTypeSchema.default('normal'), // Echo Knight echoes, etc.
    // Position
    position: PointSchema,
    rotation: z.number().default(0), // Degrees, for facing
    elevation: z.number().default(0), // Feet above ground (flying, etc.)
    // Visual properties
    visuals: TokenVisualsSchema,
    // Combat state (synced from Combatant when linked)
    currentHp: z.number().optional(),
    maxHp: z.number().optional(),
    hpTier: HpTierSchema.optional(),
    ac: z.number().optional(),
    conditions: z.array(ActiveConditionSchema).default([]),
    // Vision & Lighting
    vision: VisionConfigSchema.optional(), // Creature's vision abilities
    emitsLight: LightPresetSchema.optional(), // Light source this token carries/emits
    // UI state
    selected: z.boolean().default(false),
    visible: z.boolean().default(true), // Hidden from players?
    locked: z.boolean().default(false), // Prevent movement?
    // Ownership
    ownerId: z.string().optional(), // Player who controls this token
    isPlayerControlled: z.boolean().default(false),
    // Metadata
    createdAt: z.date().default(() => new Date()),
    updatedAt: z.date().default(() => new Date()),
});
// =============================================================================
// TOKEN ACTIONS
// =============================================================================
/**
 * Token movement action
 */
export const TokenMoveSchema = z.object({
    tokenId: z.string(),
    from: PointSchema,
    to: PointSchema,
    snapToGrid: z.boolean().default(true),
});
/**
 * Token creation params
 */
export const CreateTokenSchema = z.object({
    name: z.string(),
    position: PointSchema,
    visuals: TokenVisualsSchema.optional(),
    combatantId: z.string().optional(),
    ownerId: z.string().optional(),
    tokenType: TokenTypeSchema.optional(),
    // Optional initial combat state
    currentHp: z.number().optional(),
    maxHp: z.number().optional(),
    ac: z.number().optional(),
    // Vision & Lighting
    vision: VisionConfigSchema.optional(),
    emitsLight: LightPresetSchema.optional(),
});
/**
 * Token update params
 */
export const UpdateTokenSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    position: PointSchema.optional(),
    rotation: z.number().optional(),
    elevation: z.number().optional(),
    visuals: TokenVisualsSchema.partial().optional(),
    currentHp: z.number().optional(),
    maxHp: z.number().optional(),
    ac: z.number().optional(),
    conditions: z.array(ActiveConditionSchema).optional(),
    visible: z.boolean().optional(),
    locked: z.boolean().optional(),
    vision: VisionConfigSchema.optional(),
    emitsLight: LightPresetSchema.optional(),
});
// =============================================================================
// TOKEN PRESETS
// =============================================================================
/**
 * Common token presets for quick creation
 */
export const TOKEN_PRESETS = {
    player: {
        shape: 'circle',
        size: 'medium',
        color: '#4a90d9', // Blue
        showHpBar: true,
    },
    ally: {
        shape: 'circle',
        size: 'medium',
        color: '#5cb85c', // Green
        showHpBar: true,
    },
    enemy: {
        shape: 'circle',
        size: 'medium',
        color: '#d9534f', // Red
        showHpBar: true,
    },
    boss: {
        shape: 'diamond',
        size: 'large',
        color: '#8b0000', // Dark red
        showHpBar: true,
    },
    npc: {
        shape: 'circle',
        size: 'medium',
        color: '#f0ad4e', // Orange
        showHpBar: false,
    },
    object: {
        shape: 'square',
        size: 'medium',
        color: '#6c757d', // Gray
        showHpBar: false,
        showConditionIcons: false,
    },
};
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
/**
 * Create a token from minimal params with sensible defaults
 */
export function createToken(params) {
    const id = crypto.randomUUID();
    const now = new Date();
    return {
        id,
        name: params.name,
        combatantId: params.combatantId,
        tokenType: params.tokenType || 'normal',
        position: params.position,
        rotation: 0,
        elevation: 0,
        visuals: {
            shape: 'circle',
            size: 'medium',
            color: '#d4af37',
            showHpBar: true,
            showConditionIcons: true,
            ...params.visuals,
        },
        currentHp: params.currentHp,
        maxHp: params.maxHp,
        ac: params.ac,
        conditions: [],
        vision: params.vision || DEFAULT_VISION,
        emitsLight: params.emitsLight,
        selected: false,
        visible: true,
        locked: false,
        ownerId: params.ownerId,
        isPlayerControlled: !!params.ownerId,
        createdAt: now,
        updatedAt: now,
    };
}
/**
 * Get grid-snapped position
 */
export function snapToGrid(position, gridSize) {
    return {
        x: Math.round(position.x / gridSize) * gridSize,
        y: Math.round(position.y / gridSize) * gridSize,
    };
}
/**
 * Calculate token center based on size
 */
export function getTokenCenter(position, size, gridSize) {
    const squares = TOKEN_SIZE_SQUARES[size];
    const offset = (squares * gridSize) / 2;
    return {
        x: position.x + offset,
        y: position.y + offset,
    };
}
/**
 * Check if two tokens overlap
 */
export function tokensOverlap(a, b, gridSize) {
    const aSquares = TOKEN_SIZE_SQUARES[a.visuals.size];
    const bSquares = TOKEN_SIZE_SQUARES[b.visuals.size];
    const aRight = a.position.x + aSquares * gridSize;
    const aBottom = a.position.y + aSquares * gridSize;
    const bRight = b.position.x + bSquares * gridSize;
    const bBottom = b.position.y + bSquares * gridSize;
    return !(aRight <= b.position.x || a.position.x >= bRight ||
        aBottom <= b.position.y || a.position.y >= bBottom);
}
//# sourceMappingURL=token-types.js.map